'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  X,
  Pill,
  Clock,
  Plus,
  Trash2,
  Bell,
  Calendar,
  Building2,
  User,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useCreateMedication, useUpdateMedication } from '@/features/medications/hooks/useMedications';
import { useToast } from '@/core/hooks/useToast';
import type { Medication } from '@/features/health/types/healthTypes';

const medicationSchema = z.object({
  name: z.string().min(1, 'Medication name is required'),
  genericName: z.string().optional(),
  dosage: z.string().min(1, 'Dosage is required'),
  frequency: z.string().min(1, 'Frequency is required'),
  route: z.string().optional(),
  prescribingDoctor: z.string().optional(),
  pharmacy: z.string().optional(),
  rxNumber: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  instructions: z.string().optional(),
  sideEffects: z.string().optional(),
  reminderEnabled: z.boolean().default(false),
  reminderTimes: z.array(z.string()).default([]),
});

type MedicationFormData = z.infer<typeof medicationSchema>;

interface MedicationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  childId: string;
  medication?: Medication | null;
  onSuccess?: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: 'once_daily', label: 'Once daily' },
  { value: 'twice_daily', label: 'Twice daily' },
  { value: 'three_daily', label: 'Three times daily' },
  { value: 'four_daily', label: 'Four times daily' },
  { value: 'every_4_hours', label: 'Every 4 hours' },
  { value: 'every_6_hours', label: 'Every 6 hours' },
  { value: 'every_8_hours', label: 'Every 8 hours' },
  { value: 'every_12_hours', label: 'Every 12 hours' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'as_needed', label: 'As needed (PRN)' },
  { value: 'custom', label: 'Custom schedule' },
];

const ROUTE_OPTIONS = [
  { value: 'oral', label: 'Oral (by mouth)' },
  { value: 'sublingual', label: 'Sublingual (under tongue)' },
  { value: 'topical', label: 'Topical (on skin)' },
  { value: 'injection', label: 'Injection' },
  { value: 'inhalation', label: 'Inhalation' },
  { value: 'rectal', label: 'Rectal' },
  { value: 'ophthalmic', label: 'Ophthalmic (eye)' },
  { value: 'otic', label: 'Otic (ear)' },
  { value: 'nasal', label: 'Nasal' },
  { value: 'transdermal', label: 'Transdermal (patch)' },
  { value: 'other', label: 'Other' },
];

export function MedicationFormModal({
  isOpen,
  onClose,
  childId,
  medication,
  onSuccess,
}: MedicationFormModalProps): React.ReactElement {
  const { showToast } = useToast();
  const createMedication = useCreateMedication();
  const updateMedication = useUpdateMedication();
  
  const [reminderTimes, setReminderTimes] = useState<string[]>(
    medication?.reminderTimes || ['08:00']
  );

  const isEditing = !!medication;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MedicationFormData>({
    resolver: zodResolver(medicationSchema),
    defaultValues: {
      name: medication?.name || '',
      genericName: medication?.genericName || '',
      dosage: medication?.dosage || '',
      frequency: medication?.frequency || '',
      route: medication?.route || 'oral',
      prescribingDoctor: medication?.prescribingDoctor || '',
      pharmacy: medication?.pharmacy || '',
      rxNumber: medication?.rxNumber || '',
      startDate: medication?.startDate || '',
      endDate: medication?.endDate || '',
      instructions: medication?.instructions || '',
      sideEffects: medication?.sideEffects || '',
      reminderEnabled: medication?.reminderEnabled || false,
      reminderTimes: medication?.reminderTimes || [],
    },
  });

  const reminderEnabled = watch('reminderEnabled');

  // Reset form when medication changes
  useEffect(() => {
    if (medication) {
      reset({
        name: medication.name,
        genericName: medication.genericName || '',
        dosage: medication.dosage,
        frequency: medication.frequency,
        route: medication.route || 'oral',
        prescribingDoctor: medication.prescribingDoctor || '',
        pharmacy: medication.pharmacy || '',
        rxNumber: medication.rxNumber || '',
        startDate: medication.startDate || '',
        endDate: medication.endDate || '',
        instructions: medication.instructions || '',
        sideEffects: medication.sideEffects || '',
        reminderEnabled: medication.reminderEnabled || false,
        reminderTimes: medication.reminderTimes || [],
      });
      setReminderTimes(medication.reminderTimes || ['08:00']);
    } else {
      reset({
        name: '',
        genericName: '',
        dosage: '',
        frequency: '',
        route: 'oral',
        prescribingDoctor: '',
        pharmacy: '',
        rxNumber: '',
        startDate: '',
        endDate: '',
        instructions: '',
        sideEffects: '',
        reminderEnabled: false,
        reminderTimes: [],
      });
      setReminderTimes(['08:00']);
    }
  }, [medication, reset]);

  const addReminderTime = () => {
    if (reminderTimes.length < 10) {
      setReminderTimes([...reminderTimes, '12:00']);
    }
  };

  const removeReminderTime = (index: number) => {
    setReminderTimes(reminderTimes.filter((_, i) => i !== index));
  };

  const updateReminderTime = (index: number, value: string) => {
    const newTimes = [...reminderTimes];
    newTimes[index] = value;
    setReminderTimes(newTimes);
  };

  const onSubmit = async (data: MedicationFormData) => {
    try {
      const payload = {
        ...data,
        childId,
        reminderTimes: data.reminderEnabled ? reminderTimes : [],
      };

      if (isEditing && medication) {
        await updateMedication.mutateAsync({
          id: medication.id,
          ...payload,
        });
        showToast({
          type: 'success',
          title: 'Medication updated',
          message: `${data.name} has been updated.`,
        });
      } else {
        await createMedication.mutateAsync(payload);
        showToast({
          type: 'success',
          title: 'Medication added',
          message: `${data.name} has been added.`,
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: isEditing
          ? 'Failed to update medication.'
          : 'Failed to add medication.',
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="flex items-center justify-between p-6 border-b border-warm-gray/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
            <Pill className="w-5 h-5 text-sage" />
          </div>
          <div>
            <h2 className="text-lg font-display font-semibold text-navy">
              {isEditing ? 'Edit Medication' : 'Add Medication'}
            </h2>
            <p className="text-sm text-warm-gray">
              {isEditing
                ? 'Update medication details'
                : 'Enter medication information'}
            </p>
          </div>
        </div>
        <button
          onClick={handleClose}
          disabled={isSubmitting}
          className="p-2 hover:bg-cream rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-warm-gray" />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-navy uppercase tracking-wide">
              Basic Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">
                  Medication Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="e.g., Keppra, Baclofen"
                  error={!!errors.name}
                />
                {errors.name && (
                  <p className="text-xs text-red-500">{errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="genericName">Generic Name</Label>
                <Input
                  id="genericName"
                  {...register('genericName')}
                  placeholder="e.g., Levetiracetam"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="dosage">
                  Dosage <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dosage"
                  {...register('dosage')}
                  placeholder="e.g., 250mg, 5ml"
                  error={!!errors.dosage}
                />
                {errors.dosage && (
                  <p className="text-xs text-red-500">{errors.dosage.message}</p>
                )}
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="frequency">
                  Frequency <span className="text-red-500">*</span>
                </Label>
                <Select id="frequency" {...register('frequency')} error={!!errors.frequency}>
                  <option value="">Select frequency...</option>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                {errors.frequency && (
                  <p className="text-xs text-red-500">{errors.frequency.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="route">Route of Administration</Label>
              <Select id="route" {...register('route')}>
                {ROUTE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Prescriber & Pharmacy */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-navy uppercase tracking-wide">
              Prescriber & Pharmacy
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="prescribingDoctor" className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Prescribing Doctor
                </Label>
                <Input
                  id="prescribingDoctor"
                  {...register('prescribingDoctor')}
                  placeholder="Dr. Smith"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="pharmacy" className="flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  Pharmacy
                </Label>
                <Input
                  id="pharmacy"
                  {...register('pharmacy')}
                  placeholder="CVS, Walgreens, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="rxNumber">Rx Number</Label>
                <Input
                  id="rxNumber"
                  {...register('rxNumber')}
                  placeholder="Prescription #"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="startDate" className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Start Date
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  {...register('startDate')}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  {...register('endDate')}
                />
              </div>
            </div>
          </div>

          {/* Instructions & Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-navy uppercase tracking-wide">
              Instructions & Notes
            </h3>
            
            <div className="space-y-1.5">
              <Label htmlFor="instructions">Special Instructions</Label>
              <Textarea
                id="instructions"
                {...register('instructions')}
                placeholder="Take with food, avoid grapefruit juice, etc."
                rows={2}
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="sideEffects">Known Side Effects</Label>
              <Textarea
                id="sideEffects"
                {...register('sideEffects')}
                placeholder="Drowsiness, nausea, etc."
                rows={2}
              />
            </div>
          </div>

          {/* Reminders */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wide flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Reminders
              </h3>
              <Switch
                checked={reminderEnabled}
                onChange={(checked) => setValue('reminderEnabled', checked)}
              />
            </div>

            {reminderEnabled && (
              <div className="space-y-3 p-4 bg-cream/50 rounded-lg">
                <p className="text-sm text-warm-gray">
                  Set reminder times for this medication
                </p>
                
                <div className="space-y-2">
                  {reminderTimes.map((time, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warm-gray" />
                      <Input
                        type="time"
                        value={time}
                        onChange={(e) => updateReminderTime(index, e.target.value)}
                        className="w-32"
                      />
                      {reminderTimes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeReminderTime(index)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {reminderTimes.length < 10 && (
                  <button
                    type="button"
                    onClick={addReminderTime}
                    className="flex items-center gap-1 text-sm text-sage hover:text-sage-dark font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    Add reminder time
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-warm-gray/10">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                {isEditing ? 'Saving...' : 'Adding...'}
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Add Medication'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default MedicationFormModal;


